import React, { useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Badge } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faCalendarDays, 
    faCar, 
    faTools, 
    faBuilding, 
    faMapMarkerAlt,
    faEdit,
    faTrash,
    faEye,
    faTimes,
    faClock,
    faUsers,
    faNoteSticky,
    faWrench,
    faHeadset,
    faCheckCircle,
    faExclamationTriangle,
    faCalendarXmark,
    faHourglassHalf
} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import 'moment/locale/it';

moment.locale('it');

const AppointmentDetailsModal = ({ 
    isOpen, 
    toggle, 
    appointment, 
    onEdit, 
    onDelete, 
    onViewDetails 
}) => {
    // Stato per la modale di conferma modifica
    const [showEditConfirm, setShowEditConfirm] = useState(false);
    
    if (!appointment) return null;
    
    // Verifica se l'appuntamento fa parte di una schedulazione multi-giorno
    const isMultiDaySchedule = appointment.groupId || appointment.planningId || appointment.schedulingId || 
        (appointment.relatedAppointments && appointment.relatedAppointments.length > 0);

    // Calcola ore previste dalla durata
    const calculateHours = () => {
        if (appointment.duration) {
            const hours = Math.floor(appointment.duration / 60);
            const minutes = appointment.duration % 60;
            return `${hours}h ${minutes > 0 ? `${minutes}m` : ''}`;
        }
        if (appointment.fromDate && appointment.toDate) {
            const from = moment(appointment.fromDate);
            const to = moment(appointment.toDate);
            const diffMinutes = to.diff(from, 'minutes');
            const hours = Math.floor(diffMinutes / 60);
            const minutes = diffMinutes % 60;
            return `${hours}h ${minutes > 0 ? `${minutes}m` : ''}`;
        }
        return 'N/D';
    };
    
    // Calcola ore caricate dai closingDetails (array separato nell'appuntamento)
    // Mostra tutti i closingDetails ma segnala chi non è tra i collaboratori assegnati
    const getClosingHours = () => {
        if (!appointment.closingDetails || appointment.closingDetails.length === 0) return null;

        const assignedUserIds = appointment.users?.map(u => u.userId) || [];

        return appointment.closingDetails
            .filter(cd => cd.workHours > 0 || cd.workMinutes > 0 || cd.overtimeHours > 0 || cd.overtimeMinutes > 0)
            .map(cd => {
                // Trova il colore dell'utente dalla lista users
                const user = appointment.users?.find(u => u.userId === cd.userId);
                const isAssigned = assignedUserIds.includes(cd.userId);
                return {
                    userId: cd.userId,
                    userLabel: cd.userLabel,
                    userColor: user?.userColor || '#2196f3',
                    isAssigned,
                    hours: cd.workHours || 0,
                    minutes: cd.workMinutes || 0,
                    overtimeHours: cd.overtimeHours || 0,
                    overtimeMinutes: cd.overtimeMinutes || 0,
                    notes: cd.notes,
                    isClosed: cd.isClosed,
                    closingStatusId: cd.closingStatusId,
                    closingStatusLabel: cd.closingStatusLabel
                };
            });
    };
    
    const closingHoursData = getClosingHours();

    // Formatta data e ora
    const formatDateTime = (date) => {
        if (!date) return 'N/D';
        return moment(date).format('DD MMM YYYY, HH:mm');
    };

    // Formatta solo la data
    const formatDate = (date) => {
        if (!date) return 'N/D';
        return moment(date).format('DD/MM/YYYY');
    };
    
    // Gestione click elimina - apre direttamente la modale di eliminazione con notifiche
    const handleDeleteClick = () => {
        onDelete && onDelete(appointment, isMultiDaySchedule ? 'multi' : 'single');
    };
    
    // Gestione click modifica
    const handleEditClick = () => {
        if (isMultiDaySchedule) {
            setShowEditConfirm(true);
        } else {
            onEdit && onEdit(appointment, 'single');
        }
    };
    
    // Conferma modifica
    const confirmEdit = (type) => {
        setShowEditConfirm(false);
        onEdit && onEdit(appointment, type);
    };

    // Colore primary del tema (nero)
    const primaryColor = 'rgb(56, 56, 56)';
    const primaryColorLight = 'rgba(56, 56, 56, 0.15)';

    // Componente per le sezioni
    const Section = ({ icon, title, children, className = '' }) => (
        <div className={`mb-3 ${className}`}>
            <div className="d-flex align-items-center mb-2">
                <FontAwesomeIcon icon={icon} className="me-2" style={{ width: '16px', color: primaryColor }} />
                <span className="fw-semibold text-secondary" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {title}
                </span>
            </div>
            <div className="ps-4">
                {children}
            </div>
        </div>
    );

    // Componente per i tag colorati (utenti) con ore caricate
    const UserTagWithHours = ({ user, closingData }) => {
        // Match per userId
        const userClosing = closingData?.find(c => c.userId === user.userId);
        
        // Calcola ore totali (lavoro + straordinario)
        const totalHours = userClosing ? (userClosing.hours + userClosing.overtimeHours) : 0;
        const totalMinutes = userClosing ? (userClosing.minutes + userClosing.overtimeMinutes) : 0;
        const hasOvertime = userClosing && (userClosing.overtimeHours > 0 || userClosing.overtimeMinutes > 0);
        
        return (
            <div 
                className="d-flex align-items-center justify-content-between mb-2 px-3 py-2 rounded"
                style={{ 
                    backgroundColor: user.userColor ? `${user.userColor}15` : '#f8f9fa',
                    border: `1px solid ${user.userColor || '#dee2e6'}`,
                }}
            >
                <div className="d-flex align-items-center">
                    <span 
                        className="rounded-circle me-2"
                        style={{ 
                            width: '10px', 
                            height: '10px', 
                            backgroundColor: user.userColor || '#2196f3',
                            display: 'inline-block'
                        }}
                    />
                    <span className="fw-medium" style={{ fontSize: '0.9rem' }}>{user.userLabel}</span>
                </div>
                {userClosing && (
                    <div className="d-flex align-items-center gap-1">
                        <Badge color="success" className="px-2 py-1">
                            <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                            {userClosing.hours}h {userClosing.minutes > 0 ? `${userClosing.minutes}m` : ''}
                        </Badge>
                        {hasOvertime && (
                            <Badge color="warning" className="px-2 py-1" title="Straordinario">
                                +{userClosing.overtimeHours}h {userClosing.overtimeMinutes > 0 ? `${userClosing.overtimeMinutes}m` : ''}
                            </Badge>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // Componente per i tag risorse (veicoli/attrezzature)
    const ResourceTag = ({ label }) => (
        <Badge 
            color="light" 
            className="me-1 mb-1 text-dark border"
            style={{ fontSize: '0.75rem', fontWeight: 'normal' }}
        >
            {label}
        </Badge>
    );
    
    // Modale conferma modifica
    const EditConfirmModal = () => (
        <Modal isOpen={showEditConfirm} toggle={() => setShowEditConfirm(false)} centered size="md">
            <ModalHeader toggle={() => setShowEditConfirm(false)} className="border-0 pb-0">
                <div className="d-flex align-items-center" style={{ color: primaryColor }}>
                    <FontAwesomeIcon icon={faEdit} className="me-2" />
                    <span>Modifica Appuntamento</span>
                </div>
            </ModalHeader>
            <ModalBody className="pt-3">
                <p className="mb-3">
                    Questo appuntamento fa parte di una <strong>pianificazione su più giorni</strong>.
                </p>
                <p className="text-muted mb-4">Cosa vuoi modificare?</p>
                
                <div className="d-grid gap-2">
                    <Button 
                        outline
                        className="d-flex align-items-center justify-content-center py-3"
                        onClick={() => confirmEdit('single')}
                        style={{ borderColor: primaryColor, color: primaryColor }}
                    >
                        <FontAwesomeIcon icon={faCalendarDays} className="me-2" />
                        <div className="text-start">
                            <div className="fw-medium">Solo questo appuntamento</div>
                            <small className="text-muted">Modifica solo la data del {formatDate(appointment.fromDate)}</small>
                        </div>
                    </Button>
                    
                    <Button 
                        className="d-flex align-items-center justify-content-center py-3"
                        onClick={() => confirmEdit('all')}
                        style={{ backgroundColor: primaryColor, borderColor: primaryColor, color: '#fff' }}
                    >
                        <FontAwesomeIcon icon={faEdit} className="me-2" />
                        <div className="text-start">
                            <div className="fw-medium">Tutta la pianificazione</div>
                            <small style={{ opacity: 0.8 }}>Modifica tutti gli appuntamenti correlati</small>
                        </div>
                    </Button>
                </div>
            </ModalBody>
            <ModalFooter className="border-0 pt-0">
                <Button color="light" onClick={() => setShowEditConfirm(false)}>
                    Annulla
                </Button>
            </ModalFooter>
        </Modal>
    );

    return (
        <>
            <Modal isOpen={isOpen && !showEditConfirm} toggle={toggle} size="lg" centered>
                <ModalHeader toggle={toggle} className="border-0 pb-0">
                    <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faCalendarDays} className="me-2" style={{ color: primaryColor }} />
                        <span>Dettagli Appuntamento</span>
                        {isMultiDaySchedule && (
                            <Badge className="ms-2" style={{ fontSize: '0.7rem', backgroundColor: primaryColor, color: '#fff' }}>
                                Pianificazione multi-giorno
                            </Badge>
                        )}
                    </div>
                </ModalHeader>
                
                <ModalBody className="pt-2">
                    {/* Header con codice e cliente */}
                    <div className="bg-light rounded p-3 mb-3">
                        <div className="d-flex justify-content-between align-items-start">
                            <div>
                                <h5 className="mb-1 fw-bold" style={{ color: '#2c3e50' }}>
                                    {appointment.jobCode || 'N/D'}
                                    <span className="fw-normal text-muted ms-2">-</span>
                                    <span className="fw-normal ms-2">{appointment.customerLabel || 'Cliente N/D'}</span>
                                </h5>
                                <div className="d-flex align-items-center flex-wrap gap-2 mt-2">
                                    {appointment.jobTypeLabel && (
                                        <Badge className="px-2 py-1" style={{ backgroundColor: primaryColor, color: '#fff' }}>
                                            {appointment.jobTypeLabel}
                                        </Badge>
                                    )}
                                    {appointment.assistanceRequestCode && (
                                        <Badge color="info" className="px-2 py-1">
                                            <FontAwesomeIcon icon={faHeadset} className="me-1" />
                                            {appointment.assistanceRequestCode}
                                        </Badge>
                                    )}
                                    {appointment.maintenanceCode && (
                                        <Badge color="warning" className="px-2 py-1">
                                            <FontAwesomeIcon icon={faWrench} className="me-1" />
                                            {appointment.maintenanceCode}
                                        </Badge>
                                    )}
                                </div>
                            </div>
                            <div className="text-end">
                                <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                                    {formatDateTime(appointment.fromDate)}
                                </div>
                                <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                                    → {formatDateTime(appointment.toDate)}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        {/* Colonna sinistra */}
                        <div className="col-md-6">
                            {/* Impianto */}
                            <Section icon={faBuilding} title="Impianto">
                                <div className="fw-medium">{appointment.systemLabel || 'N/D'}</div>
                                {appointment.systemTypeList && appointment.systemTypeList.length > 0 && (
                                    <div className="mt-1">
                                        {appointment.systemTypeList.map((type, idx) => (
                                            <Badge key={idx} color="secondary" className="me-1" style={{ fontSize: '0.7rem' }}>
                                                {type.typeLabel}
                                            </Badge>
                                        ))}
                                    </div>
                                )}
                                {appointment.systemAddressLabel && (
                                    <div className="text-muted mt-1" style={{ fontSize: '0.85rem' }}>
                                        <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
                                        {appointment.systemAddressLabel}
                                    </div>
                                )}
                            </Section>

                            {/* Collaboratori con ore caricate */}
                            <Section icon={faUsers} title={`Collaboratori (${appointment.users?.length || 0})`}>
                                {appointment.users && appointment.users.length > 0 ? (
                                    <div>
                                        {appointment.users.map((user, idx) => (
                                            <UserTagWithHours key={idx} user={user} closingData={closingHoursData} />
                                        ))}
                                    </div>
                                ) : (
                                    <span className="text-muted">Nessun collaboratore assegnato</span>
                                )}
                            </Section>
                            
                            {/* Riepilogo ore caricate se presenti */}
                            {closingHoursData && closingHoursData.length > 0 && (
                                <div className="bg-success bg-opacity-10 border border-success rounded p-2 mb-3">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center">
                                            <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" />
                                            <span className="fw-medium text-success">Totale Ore Lavorate</span>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="fw-bold text-success">
                                                {closingHoursData.reduce((acc, c) => acc + c.hours, 0)}h{' '}
                                                {closingHoursData.reduce((acc, c) => acc + c.minutes, 0) > 0 && 
                                                    `${closingHoursData.reduce((acc, c) => acc + c.minutes, 0)}m`
                                                }
                                            </span>
                                            {closingHoursData.some(c => c.overtimeHours > 0 || c.overtimeMinutes > 0) && (
                                                <span className="fw-medium text-warning" title="Straordinari totali">
                                                    (+{closingHoursData.reduce((acc, c) => acc + (c.overtimeHours || 0), 0)}h{' '}
                                                    {closingHoursData.reduce((acc, c) => acc + (c.overtimeMinutes || 0), 0) > 0 && 
                                                        `${closingHoursData.reduce((acc, c) => acc + (c.overtimeMinutes || 0), 0)}m`
                                                    } straord.)
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {/* Dettagli per utente con stato chiusura */}
                                    <div className="mt-2 pt-2 border-top border-success border-opacity-25">
                                        {closingHoursData.map((cd, idx) => (
                                            <div key={idx} className="d-flex align-items-center justify-content-between py-1" style={{ fontSize: '0.85rem' }}>
                                                <div className="d-flex align-items-center gap-2">
                                                    <span
                                                        className="rounded-circle"
                                                        style={{
                                                            width: 8,
                                                            height: 8,
                                                            backgroundColor: cd.isAssigned ? cd.userColor : '#adb5bd',
                                                            display: 'inline-block'
                                                        }}
                                                    />
                                                    <span className={cd.isAssigned ? 'text-muted' : 'text-muted fst-italic'}>{cd.userLabel}</span>
                                                    {!cd.isAssigned && (
                                                        <span className="badge bg-warning bg-opacity-25 text-warning" style={{ fontSize: '0.7rem' }} title="Ore caricate dall'ufficio per un collaboratore non assegnato a questo appuntamento">
                                                            caricate da ufficio
                                                        </span>
                                                    )}
                                                    <span className="fw-medium">{cd.hours}h {cd.minutes > 0 && `${cd.minutes}m`}</span>
                                                </div>
                                                <div className="d-flex align-items-center gap-2">
                                                    {cd.closingStatusLabel && (
                                                        <Badge color="info" className="text-capitalize" pill>
                                                            {cd.closingStatusLabel}
                                                        </Badge>
                                                    )}
                                                    {cd.isClosed && (
                                                        <Badge color="success" pill>
                                                            <FontAwesomeIcon icon={faCheckCircle} className="me-1" size="xs" />
                                                            Chiuso
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Colonna destra */}
                        <div className="col-md-6">
                            {/* Info temporali */}
                            <Section icon={faClock} title="Dettagli">
                                <div className="row g-2">
                                    <div className="col-6">
                                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>Ore previste</div>
                                        <div className="fw-medium">{calculateHours()}</div>
                                    </div>
                                    <div className="col-6">
                                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>N° Addetti</div>
                                        <div className="fw-medium">{appointment.users?.length || 0}</div>
                                    </div>
                                    <div className="col-6">
                                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>Creato il</div>
                                        <div className="fw-medium">{formatDate(appointment.createDate)}</div>
                                    </div>
                                    <div className="col-6">
                                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>Scadenza</div>
                                        <div className="fw-medium">{formatDate(appointment.expirationDate) || 'N/D'}</div>
                                    </div>
                                </div>
                            </Section>

                            {/* Veicoli */}
                            <Section icon={faCar} title={`Veicoli (${appointment.vehicles?.length || 0})`}>
                                {appointment.vehicles && appointment.vehicles.length > 0 ? (
                                    <div>
                                        {appointment.vehicles.map((v, idx) => (
                                            <ResourceTag key={idx} label={v.vehicleLabel} />
                                        ))}
                                    </div>
                                ) : (
                                    <span className="text-muted">Nessun veicolo assegnato</span>
                                )}
                            </Section>

                            {/* Attrezzature */}
                            <Section icon={faTools} title={`Attrezzature (${appointment.equipments?.length || 0})`}>
                                {appointment.equipments && appointment.equipments.length > 0 ? (
                                    <div>
                                        {appointment.equipments.map((e, idx) => (
                                            <ResourceTag key={idx} label={e.equipmentLabel} />
                                        ))}
                                    </div>
                                ) : (
                                    <span className="text-muted">Nessuna attrezzatura assegnata</span>
                                )}
                            </Section>
                        </div>
                    </div>

                    {/* Note Appuntamento */}
                    <Section icon={faNoteSticky} title="Note Appuntamento" className="mt-2 border-top pt-3">
                        <div className="bg-light rounded p-2" style={{ fontSize: '0.9rem' }}>
                            {appointment.notes ? appointment.notes : <span className="text-muted fst-italic">Nessuna nota</span>}
                        </div>
                    </Section>
                    
                    {/* Note Commessa */}
                    {appointment.jobId && (
                        <Section icon={faNoteSticky} title="Note Commessa" className="mt-2">
                            <div className="bg-warning bg-opacity-10 border border-warning rounded p-2" style={{ fontSize: '0.9rem' }}>
                                {appointment.jobNotes || appointment.jobDescription ? (
                                    <>
                                        {appointment.jobDescription && (
                                            <div className="mb-1"><strong>Descrizione:</strong> {appointment.jobDescription}</div>
                                        )}
                                        {appointment.jobNotes && (
                                            <div><strong>Note:</strong> {appointment.jobNotes}</div>
                                        )}
                                    </>
                                ) : (
                                    <span className="text-muted fst-italic">Nessuna nota sulla commessa</span>
                                )}
                            </div>
                        </Section>
                    )}
                    
                </ModalBody>

                <ModalFooter className="border-0 pt-0">
                    <div className="d-flex justify-content-between w-100">
                        <Button color="light" onClick={toggle}>
                            <FontAwesomeIcon icon={faTimes} className="me-1" />
                            Chiudi
                        </Button>
                        <div className="d-flex gap-2">
                            <Button outline onClick={() => onViewDetails && onViewDetails(appointment)} style={{ borderColor: primaryColor, color: primaryColor }}>
                                <FontAwesomeIcon icon={faEye} className="me-1" />
                                Dettagli Commessa
                            </Button>
                            <Button color="danger" outline onClick={handleDeleteClick}>
                                <FontAwesomeIcon icon={faTrash} className="me-1" />
                                Elimina
                            </Button>
                            <Button onClick={handleEditClick} style={{ backgroundColor: primaryColor, borderColor: primaryColor, color: '#fff' }}>
                                <FontAwesomeIcon icon={faEdit} className="me-1" />
                                Modifica Appuntamento
                            </Button>
                        </div>
                    </div>
                </ModalFooter>
            </Modal>
            
            {/* Modale di conferma modifica */}
            <EditConfirmModal />
        </>
    );
};

export default AppointmentDetailsModal;
